import React, { useState } from 'react';
import { AlertTriangle, Building2, Plus, Trash2 } from 'lucide-react';
import * as service from '../../../services/bauordnungService';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { alsEigenesKapitel } from '../bauordnungLogic';
import {
    DOKUMENTE,
    kapitelKennungAus,
    pruefeEigenesKapitel,
    type Dokumentart,
    type EigenesKapitel,
} from '../kapitel';
import type { BauordnungBundle } from '../types';

/**
 * Eigene Kapitel — firmenweit.
 *
 * Ein hier angelegtes Kapitel steht in JEDEM neuen Dokument dieser Firma zur
 * Verfuegung. Zwei Dinge folgen daraus, und beide stehen dem Anwender auch
 * auf dem Bildschirm:
 *
 * 1. Ein neues Kapitel erscheint in jedem offenen ENTWURF, auch dem eines
 *    fremden Projekts. Deshalb ist die Vorbelegung „waehlbar" und nicht
 *    „Pflicht" — sonst stuende es ueberall sofort als leerer offener Punkt.
 * 2. Freigegebene Fassungen bleiben unberuehrt: sie fuehren ihre Kapitelliste
 *    eingefroren mit sich.
 *
 * Die Kennung wird EINMAL vergeben und wandert bei einer Titelaenderung nicht
 * mit — an ihr haengen die erfassten Regelungen.
 */

interface Props {
    bundle: BauordnungBundle;
    ctx: SchreibKontext;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
}

const eingabe = 'w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-fuchsia-400 focus:outline-none';

export const EigeneKapitelReiter: React.FC<Props> = ({ bundle, ctx, schreiben }) => {
    const [titel, setTitel] = useState('');
    const [dokument, setDokument] = useState<Dokumentart>('hse');
    const [teil, setTeil] = useState('basic');
    const [nach, setNach] = useState('');
    const [pflicht, setPflicht] = useState(false);

    const vorhandene = bundle.eigeneKapitel;
    const bekannt = vorhandene.filter(k => k.dokument === dokument).map(k => k.kapitel_id);

    const entwurf: EigenesKapitel = {
        id: kapitelKennungAus(titel || 'neues Kapitel', bekannt),
        titel: titel.trim(),
        dokument,
        teil,
        nach: nach || undefined,
        pflicht,
        waehlbar: !pflicht,
        quelle: 'erfasst',
    };
    const befunde = titel.trim()
        ? pruefeEigenesKapitel(entwurf, vorhandene.map(alsEigenesKapitel))
        : [];

    const anlegen = async () => {
        if (!titel.trim() || befunde.length) return;
        const ok = await schreiben(() => service.addEigenesKapitel({
            kapitel_id: entwurf.id,
            titel: entwurf.titel,
            dokument: entwurf.dokument,
            teil: entwurf.teil,
            nach: entwurf.nach ?? null,
            pflicht: entwurf.pflicht,
            waehlbar: entwurf.waehlbar,
            quelle: ['erfasst'],
            wenn: [],
            nicht_wenn: [],
            angelegt_von_name: '',
        }, ctx));
        if (ok !== null) { setTitel(''); setNach(''); setPflicht(false); }
    };

    const teile = DOKUMENTE[dokument].teile;
    const stellen = DOKUMENTE[dokument].kapitel.filter(e => e.teil === teil);

    return (
        <div className="space-y-6 max-w-3xl">
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-2xl p-4 flex gap-3">
                <Building2 className="w-5 h-5 text-fuchsia-600 shrink-0 mt-0.5" />
                <div className="text-sm text-fuchsia-900">
                    <p className="font-bold">Diese Kapitel gelten für Ihre ganze Firma.</p>
                    <p className="mt-1 text-[12px] text-fuchsia-800 leading-relaxed">
                        Ein hier angelegtes Kapitel steht ab sofort in jedem neuen Dokument zur Verfügung,
                        auch in anderen Projekten. Bereits <strong>freigegebene</strong> Fassungen bleiben
                        unberührt; sie führen ihre Kapitelliste eingefroren mit sich.
                    </p>
                </div>
            </div>

            <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-slate-800">Neues Kapitel</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Überschrift</label>
                        <input className={`${eingabe} mt-1`} value={titel} onChange={e => setTitel(e.target.value)}
                            placeholder="z. B. Werkschutz und Bewachung" />
                        {titel.trim() && (
                            <p className="text-[11px] text-slate-400 mt-1">
                                Kennung: <code>{entwurf.id}</code>. Sie wird einmal vergeben und ändert sich
                                später nicht mehr, auch nicht bei einer neuen Überschrift.
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Dokument</label>
                        <select className={`${eingabe} mt-1`} value={dokument}
                            onChange={e => {
                                const d = e.target.value as Dokumentart;
                                setDokument(d);
                                setTeil(DOKUMENTE[d].teile[d === 'hse' ? 1 : 0].id);
                                setNach('');
                            }}
                        >
                            <option value="ordnung">Baustellenordnung</option>
                            <option value="hse">HSE-Plan</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Teil</label>
                        <select className={`${eingabe} mt-1`} value={teil}
                            onChange={e => { setTeil(e.target.value); setNach(''); }}>
                            {teile.map(t => <option key={t.id} value={t.id}>{t.titel}</option>)}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
                            Einsortieren hinter
                        </label>
                        <select className={`${eingabe} mt-1`} value={nach} onChange={e => setNach(e.target.value)}>
                            <option value="">ans Ende des Teils</option>
                            {stellen.map(e => <option key={e.kapitel} value={e.kapitel}>{e.kapitel}</option>)}
                        </select>
                    </div>
                    <label className="sm:col-span-2 flex items-start gap-2 text-[13px] text-slate-600">
                        <input type="checkbox" checked={pflicht} onChange={e => setPflicht(e.target.checked)} className="mt-1" />
                        <span>
                            Pflichtkapitel
                            <span className="block text-[11px] text-slate-400">
                                Vorbelegt ist „wählbar". Ein Pflichtkapitel erscheint sofort in jedem offenen
                                Entwurf jedes Projekts, dort dann leer und damit als offener Punkt.
                            </span>
                        </span>
                    </label>
                </div>

                {befunde.map((b, i) => (
                    <p key={i} className="text-[12px] text-rose-600 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {b.text}
                    </p>
                ))}

                <button
                    onClick={anlegen}
                    disabled={!titel.trim() || befunde.length > 0}
                    className="bg-fuchsia-600 text-white text-sm font-bold rounded-lg px-4 py-2 flex items-center gap-1.5 disabled:bg-slate-200 disabled:text-slate-400"
                >
                    <Plus className="w-4 h-4" /> Kapitel anlegen
                </button>
            </section>

            <section>
                <h3 className="text-sm font-bold text-slate-800 mb-2">
                    Angelegte Kapitel ({vorhandene.length})
                </h3>
                {!vorhandene.length && (
                    <p className="text-[12px] text-slate-400 italic border border-dashed border-slate-200 rounded-lg px-3 py-5 text-center">
                        Noch keine eigenen Kapitel. Der Katalog des Programms deckt die üblichen Gegenstände ab.
                    </p>
                )}
                <ul className="space-y-1.5">
                    {vorhandene.map(k => (
                        <li key={k.id} className="bg-white border border-slate-200 rounded-xl px-3 py-2 flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-700">{k.titel}</p>
                                <p className="text-[11px] text-slate-400">
                                    {DOKUMENTE[k.dokument].titel} · {k.teil} · {k.pflicht ? 'Pflicht' : 'wählbar'} · <code>{k.kapitel_id}</code>
                                </p>
                            </div>
                            <button onClick={() => schreiben(() => service.deleteEigenesKapitel(k.id, ctx))}
                                className="text-slate-300 hover:text-rose-500" title="Kapitel entfernen">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
};
